import { Chip } from '@nextui-org/react'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '~/components/common/Button'
import { CustomTooltip } from '~/components/common/CustomTooltip'
import { Tag } from '~/components/common/Tag'
import { Typography } from '~/components/common/Typography'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import {
  BILLABLE_FLOW_PLANS_TOOLTIP,
  isBillableFlowLicense
} from '~/components/plan/manageAccountHelpers'
import { LicenseDetailsModal } from './LicenseDetailsModal'
import {
  addBillingLicense,
  BillingAccountData,
  cancelPendingLicense,
  createLicenseWithPayment,
  CreateLicenseWithPaymentRequest,
  deleteBillingLicense,
  getBillingLicenses,
  getLicenseTypes,
  LicenseInstance,
  LicenseType,
  previewStripeSubscription,
  restoreBillingLicense,
  revokeBillingLicense,
  StripeSubscriptionPreview,
  unlinkLicenseFromStripe,
  updateBillingLicense,
  UpdateLicenseRequest
} from '~/services/superAdminService'
import { isUnlimited } from '~/utils/quotaConstants'
import { datetimeLocalToUtc, formatDate, formatForDatetimeLocalInput } from '~/utils/timezone'
import { BetterToast } from '~/utils/toast'
import {
  buildBillingPackageUpdate,
  formatBillingPackageLabel,
  getEditableBillingPackage,
  getLicenseBillingPackage
} from './accountManagementLicenseHelpers'

const SHOW_DELETED_LICENSES_KEY = 'curie-super-admin-show-deleted-licenses'

// The exact phrase the super-admin must type to enable Hard Delete. Kept short
// enough to be quick to type but long enough to be intentional.
const HARD_DELETE_CONFIRM_PHRASE = 'hard delete this license'

// Licenses Modal for managing account licenses
export const LicensesModal = ({
  isOpen,
  onClose,
  account,
  onUpdate
}: {
  isOpen: boolean
  onClose: () => void
  account: BillingAccountData | null
  onUpdate?: () => void
}) => {
  const [licenses, setLicenses] = useState<LicenseInstance[]>([])
  const [licenseTypes, setLicenseTypes] = useState<LicenseType[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [selectedLicenseType, setSelectedLicenseType] = useState<number | null>(null)
  // Format a Date object for datetime-local input (YYYY-MM-DDTHH:mm) using LOCAL time
  const formatLocalDateForInput = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }
  // Default start date to today at current time
  const getDefaultStartDate = () => {
    return formatLocalDateForInput(new Date())
  }
  // Default end date to 1 month from now at same time
  const getDefaultEndDate = () => {
    const date = new Date()
    date.setMonth(date.getMonth() + 1)
    return formatLocalDateForInput(date)
  }
  const [startDate, setStartDate] = useState(getDefaultStartDate())
  const [endDate, setEndDate] = useState(getDefaultEndDate())
  const [newLicenseQuota, setNewLicenseQuota] = useState<string>('')
  const [newBillableFlowsQuota, setNewBillableFlowsQuota] = useState<string>('')
  const [newUnlimitedQuota, setNewUnlimitedQuota] = useState(false)
  const [newUnlimitedBillableFlows, setNewUnlimitedBillableFlows] = useState(false)
  // Billing type: 'quota' for credit-based, 'billable_flow' for flow-based billing
  const [newBillingType, setNewBillingType] = useState<'quota' | 'billable_flow'>('quota')
  // Subscription ID for linking to Stripe (when adding a new license)
  const [newSubscriptionId, setNewSubscriptionId] = useState<string>('')
  const [isNewLicenseStripeLinked, setIsNewLicenseStripeLinked] = useState(false)

  // Manual recurring (no Stripe) form state
  const [newManualRecurring, setNewManualRecurring] = useState(false)
  const [newRenewalInterval, setNewRenewalInterval] = useState<number>(1)
  const [newRecurringEndDate, setNewRecurringEndDate] = useState<string>('')

  // Enterprise contract form state
  const [contractValueDollars, setContractValueDollars] = useState<string>('')
  const [billingSchedule, setBillingSchedule] = useState<string>('annually')
  const [paymentTermsDays, setPaymentTermsDays] = useState<string>('30')

  // Helper to check if license type is enterprise-migrate related
  const isEnterpriseMigrate = (licenseTypeName?: string) => {
    if (!licenseTypeName) return false
    return licenseTypeName.startsWith('enterprise-migrate')
  }

  // Update quota when license type changes - uses license type defaults
  const handleLicenseTypeChange = (licenseTypeId: number) => {
    setSelectedLicenseType(licenseTypeId)
    const selectedType = licenseTypes.find(lt => lt.id === licenseTypeId)

    // Close payment form if switching to non-Stripe license type
    // Check billing_model from database: subscription/per_seat = Stripe enabled
    const billingModel = selectedType?.billing_model || 'prepaid'
    const isStripeEnabled = billingModel === 'subscription' || billingModel === 'per_seat'
    if (!isStripeEnabled && showPaymentForm) {
      setShowPaymentForm(false)
      setPaymentLinkResult(null)
    }

    // Handle credit quota
    // For enterprise-migrate types, force unlimited quota (same as billable flows)
    // For other types, use license type defaults
    if (isEnterpriseMigrate(selectedType?.name)) {
      setNewUnlimitedQuota(true)
      setNewLicenseQuota('')
    } else if (isUnlimited(selectedType?.quota)) {
      setNewUnlimitedQuota(true)
      setNewLicenseQuota('')
    } else if (selectedType?.quota != null && selectedType.quota > 0) {
      setNewUnlimitedQuota(false)
      setNewLicenseQuota(selectedType.quota.toString())
    } else {
      setNewUnlimitedQuota(false)
      setNewLicenseQuota('0')
    }

    // Handle billable flows quota
    // For enterprise-migrate types, default to unlimited and set billing type to billable_flow
    // For other types, don't auto-check unlimited
    if (isEnterpriseMigrate(selectedType?.name)) {
      setNewUnlimitedBillableFlows(true)
      setNewBillableFlowsQuota('')
      setNewBillingType('billable_flow')
    } else if (
      selectedType?.billable_flows_limit != null &&
      selectedType.billable_flows_limit > 0 &&
      !isUnlimited(selectedType.billable_flows_limit)
    ) {
      setNewUnlimitedBillableFlows(false)
      setNewBillableFlowsQuota(selectedType.billable_flows_limit.toString())
      setNewBillingType('quota')
    } else {
      setNewUnlimitedBillableFlows(false)
      setNewBillableFlowsQuota('0')
      setNewBillingType('quota')
    }
  }

  // Handle Stripe subscription preview before applying
  const handlePreviewSubscription = async (subscriptionId: string, context: 'add' | 'edit') => {
    if (!subscriptionId || !subscriptionId.startsWith('sub_')) {
      BetterToast.error('Invalid subscription ID format. Must start with "sub_"')
      return
    }
    try {
      setIsLoadingPreview(true)
      setPreviewContext(context)
      const preview = await previewStripeSubscription(subscriptionId)
      if (preview.error) {
        BetterToast.error(`Failed to fetch subscription: ${preview.error}`)
        return
      }
      setPreviewData(preview)
      setShowPreviewModal(true)
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to preview subscription')
    } finally {
      setIsLoadingPreview(false)
    }
  }

  // Handle confirming the preview and applying the subscription
  const handleConfirmApplySubscription = async () => {
    if (!previewData || !account) return

    try {
      setIsSyncing(true)
      setShowPreviewModal(false)

      if (previewContext === 'add') {
        // Create new license with subscription
        const response = await addBillingLicense(account.id, {
          license_type_id: selectedLicenseType || licenseTypes[0]?.id || 1,
          start_date: datetimeLocalToUtc(startDate),
          end_date: datetimeLocalToUtc(endDate),
          subscription_id: previewData.subscription_id
        })
        BetterToast.success('License created and synced from Stripe')
        setIsNewLicenseStripeLinked(true)
        // Update form with synced values
        const syncedType = licenseTypes.find(lt => lt.id === response.license_type_id)
        if (syncedType) {
          setSelectedLicenseType(response.license_type_id)
        }
        if (response.start_date) {
          setStartDate(formatForDatetimeLocalInput(response.start_date))
        }
        if (response.end_date) {
          setEndDate(formatForDatetimeLocalInput(response.end_date))
        }
        const syncedQuota = response.quota
        const syncedBillableFlows = response.billable_flows_quota
        if (syncedQuota != null) {
          if (isUnlimited(syncedQuota)) {
            setNewUnlimitedQuota(true)
            setNewLicenseQuota('')
          } else {
            setNewUnlimitedQuota(false)
            setNewLicenseQuota(syncedQuota.toString())
          }
        }
        if (syncedBillableFlows != null) {
          if (isUnlimited(syncedBillableFlows)) {
            setNewUnlimitedBillableFlows(true)
            setNewBillableFlowsQuota('')
          } else {
            setNewUnlimitedBillableFlows(false)
            setNewBillableFlowsQuota(syncedBillableFlows.toString())
          }
        }
        setNewBillingType(response.billing_type || 'quota')
        setShowAddForm(false)
        await fetchLicensesAndNotify()
      } else if (editingLicense) {
        // Update existing license with subscription
        const updatedLicense = await updateBillingLicense(account.id, editingLicense.id, {
          subscription_id: previewData.subscription_id
        })
        BetterToast.success('Subscription applied and synced from Stripe')
        setEditingLicense(updatedLicense)
        // Update form with synced values
        const syncedQuota = updatedLicense.quota
        const syncedBillableFlows = updatedLicense.billable_flows_quota
        const syncedType = licenseTypes.find(lt => lt.id === updatedLicense.license_type_id)
        setEditForm({
          ...editForm,
          license_type_id: updatedLicense.license_type_id,
          start_date: updatedLicense.start_date
            ? formatForDatetimeLocalInput(updatedLicense.start_date)
            : '',
          end_date: updatedLicense.end_date
            ? formatForDatetimeLocalInput(updatedLicense.end_date)
            : '',
          quota: syncedQuota != null && !isUnlimited(syncedQuota) ? syncedQuota.toString() : '',
          billable_flows_quota:
            syncedBillableFlows != null && !isUnlimited(syncedBillableFlows)
              ? syncedBillableFlows.toString()
              : '',
          unlimited_quota: syncedQuota != null && isUnlimited(syncedQuota),
          unlimited_billable_flows: syncedBillableFlows != null && isUnlimited(syncedBillableFlows),
          subscription_id: updatedLicense.subscription_id || '',
          auto_renew: updatedLicense.auto_renew,
          billing_package: getEditableBillingPackage(updatedLicense),
          billing_type: updatedLicense.billing_type || 'quota',
          contract_end_date: updatedLicense.contract_end_date
            ? formatForDatetimeLocalInput(updatedLicense.contract_end_date)
            : '',
          contract_value_dollars:
            syncedType?.billing_model === 'custom' && updatedLicense.contract_value_cents
              ? (updatedLicense.contract_value_cents / 100).toString()
              : '',
          billing_schedule: updatedLicense.billing_schedule || 'annually',
          payment_terms_days: updatedLicense.payment_terms_days?.toString() || '30'
        })
        await fetchLicensesAndNotify()
      }
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to apply subscription')
    } finally {
      setIsSyncing(false)
      setPreviewData(null)
    }
  }

  // Handle unlink subscription
  const handleUnlinkSubscription = async (licenseId: number) => {
    if (!account) return
    try {
      setIsSyncing(true)
      const updatedLicense = await unlinkLicenseFromStripe(account.id, licenseId)
      BetterToast.success('License unlinked from Stripe - manual control enabled')
      // Update the editing license if we're editing
      if (editingLicense && editingLicense.id === licenseId) {
        setEditingLicense(updatedLicense)
        setEditForm({
          ...editForm,
          subscription_id: '',
          auto_renew: false,
          billing_package: 'monthly'
        })
      }
      await fetchLicensesAndNotify()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to unlink subscription')
    } finally {
      setIsSyncing(false)
    }
  }

  // Format currency for display
  const formatCurrency = (amountCents?: number, currency?: string) => {
    if (amountCents == null) return 'N/A'
    const amount = amountCents / 100
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'USD'
    })
    return formatter.format(amount)
  }

  // Get status color for subscription status badge
  const getSubscriptionStatusColor = (
    status?: string
  ): 'success' | 'warning' | 'danger' | 'default' => {
    switch (status) {
      case 'active':
        return 'success'
      case 'trialing':
        return 'success'
      case 'past_due':
        return 'warning'
      case 'unpaid':
        return 'danger'
      case 'canceled':
        return 'danger'
      case 'incomplete':
        return 'warning'
      case 'incomplete_expired':
        return 'danger'
      case 'paused':
        return 'warning'
      default:
        return 'default'
    }
  }

  // Handler for billing type change
  const handleBillingTypeChange = (billingType: 'quota' | 'billable_flow') => {
    setNewBillingType(billingType)
    if (billingType === 'billable_flow') {
      // For billable flow billing, set unlimited quota (N/A)
      setNewUnlimitedQuota(true)
      setNewLicenseQuota('')
    } else {
      // For quota billing, restore quota defaults from license type
      const selectedType = licenseTypes.find(lt => lt.id === selectedLicenseType)
      if (isUnlimited(selectedType?.quota)) {
        setNewUnlimitedQuota(true)
        setNewLicenseQuota('')
      } else if (selectedType?.quota != null && selectedType.quota > 0) {
        setNewUnlimitedQuota(false)
        setNewLicenseQuota(selectedType.quota.toString())
      } else {
        setNewUnlimitedQuota(false)
        setNewLicenseQuota('0')
      }
    }
  }

  // Edit state
  const [editingLicense, setEditingLicense] = useState<LicenseInstance | null>(null)
  // Add license form visibility
  const [showAddForm, setShowAddForm] = useState(false)
  // Details modal state
  const [selectedLicenseDetails, setSelectedLicenseDetails] = useState<LicenseInstance | null>(null)
  // Delete confirmation modal state
  const [licenseToDelete, setLicenseToDelete] = useState<{
    license: LicenseInstance
    permanent: boolean
  } | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  // Typed-sentence guard for Hard Delete (irrecoverable). Reset whenever the
  // confirm modal opens/closes so the next delete starts fresh.
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('')
  const [licenseToRevoke, setLicenseToRevoke] = useState<LicenseInstance | null>(null)
  const [isRevoking, setIsRevoking] = useState(false)
  const [editForm, setEditForm] = useState<{
    license_type_id: number
    start_date: string
    end_date: string
    quota: string
    billable_flows_quota: string
    subscription_id: string
    auto_renew: boolean
    billing_package: 'monthly' | 'six_month' | 'none'
    unlimited_quota: boolean
    unlimited_billable_flows: boolean
    billing_type: 'quota' | 'billable_flow'
    contract_end_date: string
    // Manual recurring fields
    manual_recurring: boolean
    renewal_interval_months: number
    recurring_end_date: string
    // Enterprise contract fields
    contract_value_dollars: string
    billing_schedule: string
    payment_terms_days: string
  }>({
    license_type_id: 0,
    start_date: '',
    end_date: '',
    quota: '',
    billable_flows_quota: '',
    subscription_id: '',
    auto_renew: true,
    billing_package: 'monthly',
    unlimited_quota: false,
    unlimited_billable_flows: false,
    billing_type: 'quota',
    contract_end_date: '',
    manual_recurring: false,
    renewal_interval_months: 1,
    recurring_end_date: '',
    // Enterprise contract fields
    contract_value_dollars: '',
    billing_schedule: '',
    payment_terms_days: ''
  })
  const [isUpdating, setIsUpdating] = useState(false)

  // Stripe sync state (used by Apply button)
  const [isSyncing, setIsSyncing] = useState(false)

  // Soft-deleted licenses are hidden by default. The toggle reveals them with a
  // "Deleted" badge; Edit/Revoke are disabled in that state. Persisted per-browser
  // so super-admins who routinely audit deleted rows don't have to re-enable it.
  const [showDeleted, setShowDeleted] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_DELETED_LICENSES_KEY) === 'true'
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem(SHOW_DELETED_LICENSES_KEY, String(showDeleted))
    } catch {
      // localStorage may be unavailable (private mode, quota); ignore.
    }
  }, [showDeleted])

  // Stripe preview state (for confirmation before applying)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewData, setPreviewData] = useState<StripeSubscriptionPreview | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [previewContext, setPreviewContext] = useState<'add' | 'edit'>('add')

  // Create license with payment state
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [isCreatingPaymentLink, setIsCreatingPaymentLink] = useState(false)
  const [paymentLinkResult, setPaymentLinkResult] = useState<{
    payment_url: string
    pending_license_id: number
    expires_at: string
  } | null>(null)
  const [paymentContractEndDate, setPaymentContractEndDate] = useState('')
  const [paymentBillingPackage, setPaymentBillingPackage] = useState<'monthly' | 'six_month'>(
    'monthly'
  )

  // Reset all form state when modal closes
  const handleModalClose = () => {
    // Reset add form state
    setShowAddForm(false)
    setSelectedLicenseType(licenseTypes[0]?.id || null)
    setStartDate(getDefaultStartDate())
    setEndDate(getDefaultEndDate())
    setNewLicenseQuota('')
    setNewBillableFlowsQuota('')
    setNewUnlimitedQuota(false)
    setNewUnlimitedBillableFlows(false)
    setNewBillingType('quota')
    setContractValueDollars('')
    setBillingSchedule('annually')
    setPaymentTermsDays('30')
    setShowPaymentForm(false)
    setPaymentLinkResult(null)
    setPaymentContractEndDate('')
    setPaymentBillingPackage('monthly')
    // Reset subscription linking state
    setNewSubscriptionId('')
    setIsNewLicenseStripeLinked(false)
    // Reset edit state
    setEditingLicense(null)
    setSelectedLicenseDetails(null)
    // Call original onClose
    onClose()
  }

  const handleCreateWithPayment = async () => {
    if (!account || !selectedLicenseType) return
    try {
      setIsCreatingPaymentLink(true)
      const quotaValue = newUnlimitedQuota
        ? -1
        : newLicenseQuota
          ? parseInt(newLicenseQuota, 10)
          : undefined
      const billableFlowsQuotaValue = newUnlimitedBillableFlows
        ? -1
        : newBillableFlowsQuota
          ? parseInt(newBillableFlowsQuota, 10)
          : undefined

      // Only send quota OR billable_flows_quota based on billing type
      const request: CreateLicenseWithPaymentRequest = {
        license_type_id: selectedLicenseType,
        quota: newBillingType === 'quota' ? quotaValue : undefined,
        billable_flows_quota:
          newBillingType === 'billable_flow' ? billableFlowsQuotaValue : undefined,
        billing_package: paymentBillingPackage,
        success_url: `${window.location.origin}/billing/success`,
        cancel_url: `${window.location.origin}/billing/cancel`
      }

      if (paymentContractEndDate) {
        request.contract_end_date = datetimeLocalToUtc(paymentContractEndDate)
      }

      const result = await createLicenseWithPayment(account.id, request)
      setPaymentLinkResult(result)
      BetterToast.success('Payment link created! Share it with the customer.')
      await fetchLicensesAndNotify()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to create payment link')
    } finally {
      setIsCreatingPaymentLink(false)
    }
  }

  const handleCancelPendingLicense = async (licenseId: number) => {
    if (!account) return
    try {
      await cancelPendingLicense(account.id, licenseId)
      BetterToast.success('Pending license canceled')
      await fetchLicensesAndNotify()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to cancel pending license')
    }
  }

  const fetchLicenses = useCallback(async () => {
    if (!account) return
    try {
      setIsLoading(true)
      const [licensesData, typesData] = await Promise.all([
        getBillingLicenses(account.id),
        getLicenseTypes()
      ])
      setLicenses(licensesData)
      setLicenseTypes(typesData)
      if (typesData.length > 0 && !selectedLicenseType) {
        setSelectedLicenseType(typesData[0].id)
        const firstType = typesData[0]
        // Set default quota from first license type
        // For enterprise-migrate types, force unlimited quota
        if (isEnterpriseMigrate(firstType.name)) {
          setNewUnlimitedQuota(true)
          setNewLicenseQuota('')
        } else if (isUnlimited(firstType.quota)) {
          setNewUnlimitedQuota(true)
          setNewLicenseQuota('')
        } else if (firstType.quota != null && firstType.quota > 0) {
          setNewUnlimitedQuota(false)
          setNewLicenseQuota(firstType.quota.toString())
        } else {
          setNewUnlimitedQuota(false)
          setNewLicenseQuota('0')
        }
        // Set default billable_flows_limit from first license type
        // For enterprise-migrate types, default to unlimited
        if (isEnterpriseMigrate(firstType.name)) {
          setNewUnlimitedBillableFlows(true)
          setNewBillableFlowsQuota('')
        } else if (
          firstType.billable_flows_limit != null &&
          firstType.billable_flows_limit > 0 &&
          !isUnlimited(firstType.billable_flows_limit)
        ) {
          setNewUnlimitedBillableFlows(false)
          setNewBillableFlowsQuota(firstType.billable_flows_limit.toString())
        } else {
          setNewUnlimitedBillableFlows(false)
          setNewBillableFlowsQuota('0')
        }
      }
    } catch {
      BetterToast.error('Failed to fetch licenses')
    } finally {
      setIsLoading(false)
    }
  }, [account, selectedLicenseType])

  const fetchLicensesAndNotify = useCallback(async () => {
    await fetchLicenses()
    onUpdate?.()
  }, [fetchLicenses, onUpdate])

  useEffect(() => {
    if (isOpen && account) {
      fetchLicenses()
    }
  }, [isOpen, account, fetchLicenses])

  // Reset edit mode and sync state when modal closes to prevent stale state
  useEffect(() => {
    if (!isOpen) {
      // Reset editing state when modal closes
      setEditingLicense(null)
      setEditForm({
        license_type_id: 0,
        start_date: '',
        end_date: '',
        quota: '',
        billable_flows_quota: '',
        subscription_id: '',
        auto_renew: true,
        billing_package: 'monthly',
        unlimited_quota: false,
        unlimited_billable_flows: false,
        billing_type: 'quota',
        contract_end_date: '',
        manual_recurring: false,
        renewal_interval_months: 1,
        recurring_end_date: '',
        contract_value_dollars: '',
        billing_schedule: '',
        payment_terms_days: ''
      })
      // Reset payment form state
      setShowPaymentForm(false)
      setPaymentLinkResult(null)
      setPaymentContractEndDate('')
      setPaymentBillingPackage('monthly')
      // Reset nested modal states
      setSelectedLicenseDetails(null)
      setLicenseToDelete(null)
    }
  }, [isOpen])

  const handleAddLicense = async () => {
    if (!account || !selectedLicenseType || !startDate || !endDate) return
    try {
      setIsAdding(true)
      // Use -1 for unlimited quotas
      const quotaValue = newUnlimitedQuota
        ? -1
        : newLicenseQuota
          ? parseInt(newLicenseQuota, 10)
          : 0
      const billableFlowsQuotaValue = newUnlimitedBillableFlows
        ? -1
        : newBillableFlowsQuota
          ? parseInt(newBillableFlowsQuota, 10)
          : 0

      // Check if this is an enterprise license
      const selectedType = licenseTypes.find(lt => lt.id === selectedLicenseType)
      const isEnterprise = selectedType?.billing_model === 'custom'

      // Build request with enterprise contract fields if applicable
      // Only send quota OR billable_flows_quota based on billing type, not both
      // Sending both causes API to incorrectly detect billable_flow plan when 0 is sent
      const request: Parameters<typeof addBillingLicense>[1] = {
        license_type_id: selectedLicenseType,
        start_date: datetimeLocalToUtc(startDate),
        end_date: datetimeLocalToUtc(endDate),
        quota: newBillingType === 'quota' ? quotaValue : undefined,
        billable_flows_quota:
          newBillingType === 'billable_flow' ? billableFlowsQuotaValue : undefined
      }

      // Add enterprise contract fields if applicable
      if (isEnterprise) {
        if (contractValueDollars) {
          request.contract_value_cents = Math.round(parseFloat(contractValueDollars) * 100)
        }
        request.billing_schedule = billingSchedule
        request.payment_terms_days = parseInt(paymentTermsDays, 10) || 30
      }

      // Add manual recurring fields if enabled
      if (newManualRecurring) {
        if (!newRecurringEndDate) {
          BetterToast.error('Recurring End Date is required when Manual Recurring is enabled')
          return
        }
        request.manual_recurring = true
        request.renewal_interval_months = newRenewalInterval
        request.recurring_end_date = datetimeLocalToUtc(newRecurringEndDate)
      }

      await addBillingLicense(account.id, request)
      const message = isEnterprise
        ? 'Enterprise license created. Stripe customer synced.'
        : 'License added. If higher tier, it now owns the billing period.'
      BetterToast.success(message)
      setNewBillableFlowsQuota('')
      // Reset unlimited checkboxes for next add
      setNewUnlimitedQuota(false)
      setNewUnlimitedBillableFlows(false)
      // Reset enterprise contract fields
      setContractValueDollars('')
      setBillingSchedule('annually')
      setPaymentTermsDays('30')
      // Reset manual recurring fields
      setNewManualRecurring(false)
      setNewRenewalInterval(1)
      setNewRecurringEndDate('')
      await fetchLicensesAndNotify()
      // Close the add form after successful add
      setShowAddForm(false)
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to add license')
    } finally {
      setIsAdding(false)
    }
  }

  // Show delete confirmation modal
  const handleDeleteLicenseClick = (license: LicenseInstance, permanent: boolean) => {
    setLicenseToDelete({ license, permanent })
    setHardDeleteConfirmText('')
  }

  // Execute the delete after confirmation
  const handleConfirmDelete = async () => {
    if (!account || !licenseToDelete) return
    setIsDeleting(true)
    try {
      const { license, permanent } = licenseToDelete

      await deleteBillingLicense(account.id, license.id, permanent)

      BetterToast.success(permanent ? 'License permanently deleted' : 'License deleted')
      setLicenseToDelete(null)
      await fetchLicensesAndNotify()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to delete license')
    } finally {
      setIsDeleting(false)
    }
  }

  // Restore a soft-deleted license. Backend clears deleted/archived flags;
  // dates are preserved, so the row's computed status is whatever its existing
  // start_date/end_date say (typically "inactive" since soft-delete pushed
  // end_date to now()). Operator can Edit afterward to extend.
  const [isRestoring, setIsRestoring] = useState<number | null>(null)
  const handleRestoreLicense = async (license: LicenseInstance) => {
    if (!account || isRestoring) return
    try {
      setIsRestoring(license.id)
      await restoreBillingLicense(account.id, license.id)
      BetterToast.success('License restored. Edit dates to make it active again.')
      await fetchLicensesAndNotify()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to restore license')
    } finally {
      setIsRestoring(null)
    }
  }

  // Execute the revoke after confirmation (sets end_date to now, making it inactive)
  const handleConfirmRevoke = async () => {
    if (!account || !licenseToRevoke) return
    setIsRevoking(true)
    try {
      // Revoke the license (sets end_date to now, transfers billing period if needed)
      await revokeBillingLicense(account.id, licenseToRevoke.id)
      BetterToast.success('License revoked - now inactive')
      setLicenseToRevoke(null)
      await fetchLicensesAndNotify()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to revoke license')
    } finally {
      setIsRevoking(false)
    }
  }

  const handleEditLicense = (license: LicenseInstance) => {
    setEditingLicense(license)
    // Check if quota is unlimited using centralized function
    const isUnlimitedQuota = isUnlimited(license.quota)
    const isUnlimitedBillableFlows = isUnlimited(license.billable_flows_quota)
    // Get the license type for default values
    const licenseType = licenseTypes.find(lt => lt.id === license.license_type_id)

    // Use the billing_type from the license directly - this is the source of truth
    const isBillableFlowBilling = license.billing_type === 'billable_flow'

    // Quota: use license instance value if set (even if 0), otherwise fall back to license type default
    let quotaValue = ''
    if (!isUnlimitedQuota) {
      // If license has a quota set (including 0), use it; only fall back to license type default if null
      if (license.quota != null) {
        quotaValue = license.quota.toString()
      } else if (licenseType?.quota != null && licenseType.quota > 0) {
        quotaValue = licenseType.quota.toString()
      } else {
        quotaValue = '0'
      }
    }
    // Billable flows quota: use license instance value if set, otherwise fall back to license type default
    let billableFlowsQuotaValue = ''
    if (!isUnlimitedBillableFlows) {
      // If license has billable_flows_quota set (including 0), use it; only fall back if null
      if (license.billable_flows_quota != null) {
        billableFlowsQuotaValue = license.billable_flows_quota.toString()
      } else if (
        licenseType?.billable_flows_limit != null &&
        licenseType.billable_flows_limit > 0
      ) {
        billableFlowsQuotaValue = licenseType.billable_flows_limit.toString()
      } else {
        billableFlowsQuotaValue = '0'
      }
    }

    // Use utility to properly convert UTC dates from backend to local time for input
    // IMPORTANT: When billing_type is 'quota', clear billable_flows values to ensure
    // they get set to 0 when saving (prevents stale billable_flows_quota from persisting)
    setEditForm({
      license_type_id: license.license_type_id,
      start_date: formatForDatetimeLocalInput(license.start_date),
      end_date: formatForDatetimeLocalInput(license.end_date),
      quota: quotaValue,
      // For quota billing, clear billable flows; for billable_flow billing, use the values from DB
      billable_flows_quota: isBillableFlowBilling ? billableFlowsQuotaValue : '0',
      subscription_id: license.subscription_id || '',
      auto_renew: license.auto_renew ?? true,
      billing_package: getEditableBillingPackage(license),
      unlimited_quota: isUnlimitedQuota,
      // For quota billing, clear unlimited_billable_flows to ensure it saves as 0
      unlimited_billable_flows: isBillableFlowBilling ? isUnlimitedBillableFlows : false,
      billing_type: license.billing_type,
      contract_end_date: license.contract_end_date
        ? formatForDatetimeLocalInput(license.contract_end_date)
        : '',
      manual_recurring: license.manual_recurring ?? false,
      renewal_interval_months: license.renewal_interval_months ?? 1,
      recurring_end_date: license.recurring_end_date
        ? formatForDatetimeLocalInput(license.recurring_end_date)
        : '',
      // Enterprise contract fields (convert cents to dollars for display)
      contract_value_dollars: license.contract_value_cents
        ? (license.contract_value_cents / 100).toString()
        : '',
      billing_schedule: license.billing_schedule || '',
      payment_terms_days: license.payment_terms_days?.toString() || ''
    })
  }

  const handleCancelEdit = () => {
    setEditingLicense(null)
    setEditForm({
      license_type_id: 0,
      start_date: '',
      end_date: '',
      quota: '',
      billable_flows_quota: '',
      subscription_id: '',
      auto_renew: true,
      billing_package: 'monthly',
      unlimited_quota: false,
      unlimited_billable_flows: false,
      billing_type: 'quota',
      contract_end_date: '',
      manual_recurring: false,
      renewal_interval_months: 1,
      recurring_end_date: '',
      // Reset enterprise fields
      contract_value_dollars: '',
      billing_schedule: '',
      payment_terms_days: ''
    })
  }

  const handleUpdateLicense = async () => {
    if (!account || !editingLicense) return
    try {
      setIsUpdating(true)
      const updateData: UpdateLicenseRequest = {}

      if (editForm.license_type_id !== editingLicense.license_type_id) {
        updateData.license_type_id = editForm.license_type_id
      }
      // Compare dates - use the same utility for consistent format
      const originalStartDate = formatForDatetimeLocalInput(editingLicense.start_date)
      const originalEndDate = formatForDatetimeLocalInput(editingLicense.end_date)
      if (editForm.start_date && editForm.start_date !== originalStartDate) {
        // Convert local time from input back to UTC for the backend
        updateData.start_date = datetimeLocalToUtc(editForm.start_date)
      }
      if (editForm.end_date && editForm.end_date !== originalEndDate) {
        // Convert local time from input back to UTC for the backend
        updateData.end_date = datetimeLocalToUtc(editForm.end_date)
      }
      // Handle quota/billable_flows based on billing_type
      // Only send the relevant field based on the selected billing type
      if (editForm.billing_type === 'quota') {
        // Credit-based billing: send quota, don't send billable_flows_quota
        const newQuota = editForm.unlimited_quota
          ? -1
          : editForm.quota.trim() !== ''
            ? parseInt(editForm.quota, 10)
            : 0
        const originalQuota = editingLicense.quota
        if (newQuota !== originalQuota) {
          updateData.quota = newQuota
        }
      } else {
        // Flow-based billing: send billable_flows_quota
        // Also set quota to unlimited (-1) if not already
        const newBillableFlowsQuota = editForm.unlimited_billable_flows
          ? -1
          : editForm.billable_flows_quota.trim() !== ''
            ? parseInt(editForm.billable_flows_quota, 10)
            : 0
        const originalBillableFlowsQuota = editingLicense.billable_flows_quota
        // Always send billable_flows_quota when switching to flow-based
        const isSwitchingToFlowBased = editingLicense.billing_type !== 'billable_flow'
        if (newBillableFlowsQuota !== originalBillableFlowsQuota || isSwitchingToFlowBased) {
          updateData.billable_flows_quota = newBillableFlowsQuota
        }
        // Set quota to unlimited for flow-based billing if not already
        const originalQuota = editingLicense.quota
        if (originalQuota !== -1 && !isUnlimited(originalQuota)) {
          updateData.quota = -1
        }
      }
      if (editForm.subscription_id !== (editingLicense.subscription_id || '')) {
        updateData.subscription_id = editForm.subscription_id
      }
      // Stripe-linked licenses are Stripe-controlled — never let the UI send
      // a billing_package override. Manual licenses: backend records the
      // cadence and clears stale credit_period_* keys on save. See
      // accountManagementLicenseHelpers.buildBillingPackageUpdate.
      const billingPackageUpdate = buildBillingPackageUpdate(
        { billing_type: editForm.billing_type, billing_package: editForm.billing_package },
        editingLicense
      )
      if (billingPackageUpdate !== undefined) {
        updateData.billing_package = billingPackageUpdate
      }
      // Billing-period ownership is derived (highest-tier active), never editable.
      // Handle auto_renew (synced with Stripe)
      if (editForm.auto_renew !== (editingLicense.auto_renew ?? true)) {
        updateData.auto_renew = editForm.auto_renew
      }
      // Handle contract_end_date (Last Incurring Date)
      const originalContractEndDate = editingLicense.contract_end_date
        ? formatForDatetimeLocalInput(editingLicense.contract_end_date)
        : ''
      if (editForm.contract_end_date !== originalContractEndDate) {
        // If empty, send empty string to clear; otherwise convert to UTC
        updateData.contract_end_date = editForm.contract_end_date
          ? datetimeLocalToUtc(editForm.contract_end_date)
          : ''
      }

      // Handle manual recurring fields
      if (editForm.manual_recurring) {
        if (!editForm.recurring_end_date) {
          BetterToast.error('Recurring End Date is required when Manual Recurring is enabled')
          return
        }
        if (!(editingLicense.manual_recurring ?? false)) {
          updateData.manual_recurring = true
        }
        // Always send renewal_interval_months when manual_recurring is on,
        // to fix null DB values that cause the badge to show "Yearly"
        if (
          editingLicense.renewal_interval_months == null ||
          editForm.renewal_interval_months !== editingLicense.renewal_interval_months
        ) {
          updateData.renewal_interval_months = editForm.renewal_interval_months
        }
        const originalRecurringEndDate = editingLicense.recurring_end_date
          ? formatForDatetimeLocalInput(editingLicense.recurring_end_date)
          : ''
        if (editForm.recurring_end_date !== originalRecurringEndDate) {
          updateData.recurring_end_date = datetimeLocalToUtc(editForm.recurring_end_date)
        }
      } else if (editingLicense.manual_recurring) {
        updateData.manual_recurring = false
      }

      // Handle enterprise contract fields (only for custom billing model)
      const editLicenseType = licenseTypes.find(lt => lt.id === editForm.license_type_id)
      if (editLicenseType?.billing_model === 'custom') {
        // Contract value (convert dollars to cents)
        const newContractValueCents = editForm.contract_value_dollars
          ? Math.round(parseFloat(editForm.contract_value_dollars) * 100)
          : null
        const originalContractValueCents = editingLicense.contract_value_cents ?? null
        if (newContractValueCents !== originalContractValueCents) {
          updateData.contract_value_cents = newContractValueCents
        }

        // Billing schedule
        const newBillingSchedule = editForm.billing_schedule || null
        const originalBillingSchedule = editingLicense.billing_schedule ?? null
        if (newBillingSchedule !== originalBillingSchedule) {
          updateData.billing_schedule = newBillingSchedule
        }

        // Payment terms
        const newPaymentTermsDays = editForm.payment_terms_days
          ? parseInt(editForm.payment_terms_days, 10)
          : null
        const originalPaymentTermsDays = editingLicense.payment_terms_days ?? null
        if (newPaymentTermsDays !== originalPaymentTermsDays) {
          updateData.payment_terms_days = newPaymentTermsDays
        }
      }

      if (Object.keys(updateData).length === 0) {
        BetterToast.success('No changes to save')
        handleCancelEdit()
        return
      }

      await updateBillingLicense(account.id, editingLicense.id, updateData)
      BetterToast.success('License updated successfully')
      handleCancelEdit()
      await fetchLicensesAndNotify()
    } catch (error: unknown) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to update license')
    } finally {
      setIsUpdating(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'inactive':
        return 'bg-gray-100 text-gray-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      hideCloseButton={false}
      className='w-[980px] max-h-[90vh]'
    >
      <ModalBody className='p-5'>
        <div className='mb-4 flex items-center justify-between'>
          <div>
            <Typography
              fontFamily='geist'
              fontSize='bodyLgMedium'
              fontWeight='semibold'
              className='text-tokens-neutral-text-primary select-none'
            >
              Licenses
            </Typography>
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              className='text-tokens-neutral-text-secondary'
            >
              {account?.name}
            </Typography>
          </div>
          <Button
            variant='primary'
            onClick={() => setShowAddForm(true)}
            className='h-8 px-3 text-sm'
          >
            + Add
          </Button>
        </div>

        {/* Licenses List */}
        <div className='rounded-[16px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]'>
          {(() => {
            const deletedCount = licenses.filter(l => l.deleted).length
            const visibleLicenses = showDeleted ? licenses : licenses.filter(l => !l.deleted)
            return (
              <>
                <div className='mb-3 flex items-center justify-between'>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodySm'
                    fontWeight='medium'
                    className='text-tokens-neutral-text-secondary select-none'
                  >
                    Active and historical licenses
                  </Typography>
                  <div className='flex items-center gap-2'>
                    {deletedCount > 0 && (
                      <label className='flex cursor-pointer items-center gap-1.5 text-xs text-tokens-neutral-text-secondary'>
                        <input
                          type='checkbox'
                          checked={showDeleted}
                          onChange={e => setShowDeleted(e.target.checked)}
                          className='h-3.5 w-3.5'
                        />
                        Show deleted ({deletedCount})
                      </label>
                    )}
                    <span className='inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-tokens-neutral-fill-fill-6 px-2 text-xs font-semibold text-tokens-neutral-text-secondary tabular-nums'>
                      {visibleLicenses.length}
                    </span>
                  </div>
                </div>
                <div className='max-h-[340px] space-y-2 overflow-y-auto pr-1'>
                  {isLoading ? (
                    <div className='p-4 text-center text-gray-500 text-sm select-none'>
                      Loading...
                    </div>
                  ) : visibleLicenses.length === 0 ? (
                    <div className='p-4 text-center text-gray-500 text-sm'>
                      No licenses. Click &quot;+ Add&quot; to create one.
                    </div>
                  ) : (
                    [...visibleLicenses]
                      .sort((a, b) => {
                        if (a.status !== b.status) {
                          if (a.status === 'active') return -1
                          if (b.status === 'active') return 1
                        }
                        if (a.is_billing_period !== b.is_billing_period) {
                          return a.is_billing_period ? -1 : 1
                        }
                        return new Date(b.end_date).getTime() - new Date(a.end_date).getTime()
                      })
                      .map(license => {
                        const licenseType = licenseTypes.find(
                          lt => lt.id === license.license_type_id
                        )
                        const isBillableFlowPlan = isBillableFlowLicense(
                          licenseType?.name ?? license.license_type_name
                        )
                        const effectiveStatus = license.status
                        const billingPackage = getLicenseBillingPackage(license)
                        const isDeleted = !!license.deleted

                        return (
                          <div
                            key={license.id}
                            className={`group flex items-center gap-4 rounded-[14px] border p-3 text-xs shadow-segment transition-shadow duration-200 ${
                              isDeleted
                                ? 'border-red-200 bg-red-50/40 opacity-60'
                                : 'border-tokens-neutral-border-2 bg-tokens-neutral-bg-white'
                            } ${
                              editingLicense?.id === license.id
                                ? 'shadow-button'
                                : !isDeleted && effectiveStatus !== 'active'
                                  ? 'opacity-65'
                                  : !isDeleted
                                    ? 'hover:shadow-button'
                                    : ''
                            }`}
                          >
                            <div className='w-[21%] min-w-0'>
                              <div className='flex min-w-0 items-center gap-2'>
                                <Typography
                                  fontFamily='geist'
                                  fontSize='bodySm'
                                  fontWeight='semibold'
                                  className='truncate text-tokens-neutral-text-primary'
                                >
                                  {license.license_type_label}
                                </Typography>
                                {isBillableFlowPlan && (
                                  <CustomTooltip content='Billable-flow plan: consumes flows, not credits.'>
                                    <span
                                      aria-label='Billable-flow plan'
                                      className='shrink-0 inline-flex h-4 w-4 items-center justify-center text-[10px]'
                                    >
                                      ⚡
                                    </span>
                                  </CustomTooltip>
                                )}
                                <span className='shrink-0 rounded-full bg-tokens-neutral-fill-fill-6 px-2 py-0.5 text-[11px] font-medium text-tokens-neutral-text-secondary'>
                                  #{license.id}
                                </span>
                              </div>
                              <div
                                className='mt-2 inline-flex rounded-full bg-tokens-status-link-base px-2 py-0.5 text-[11px] font-semibold text-tokens-status-link-2'
                                title={
                                  license.subscription_id
                                    ? 'Stripe billing package'
                                    : 'Manual license cadence'
                                }
                              >
                                {formatBillingPackageLabel(billingPackage)}
                              </div>
                            </div>
                            <div className='w-[24%] text-tokens-neutral-text-secondary'>
                              <div className='text-[11px] font-medium uppercase tracking-[0.04em] text-tokens-neutral-text-tertiary'>
                                Access period
                              </div>
                              <div>{formatDate(license.start_date)}</div>
                              <div className='whitespace-nowrap'>
                                to {formatDate(license.end_date)}
                              </div>
                              {license.contract_end_date && (
                                <div
                                  className='text-xs text-red-600 whitespace-nowrap'
                                  title='Last Incurring Date (Contract End)'
                                >
                                  ⛔ {formatDate(license.contract_end_date)}
                                </div>
                              )}
                            </div>
                            <div className='w-[20%] text-center'>
                              <div className='text-[11px] font-medium uppercase tracking-[0.04em] text-tokens-neutral-text-tertiary'>
                                {isBillableFlowPlan ? 'Flows' : 'Credits'}
                              </div>
                              {isBillableFlowPlan
                                ? isUnlimited(license.billable_flows_quota)
                                  ? 'Unlimited'
                                  : (license.billable_flows_quota?.toLocaleString() ?? 0)
                                : isUnlimited(license.quota)
                                  ? 'Unlimited'
                                  : (license.quota?.toLocaleString() ?? 0)}
                            </div>
                            <div className='w-[20%]'>
                              <div className='flex flex-col gap-1'>
                                {/* Row 1: License Status + Type badges */}
                                <div className='flex flex-wrap gap-1.5'>
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${getStatusColor(effectiveStatus)}`}
                                    title={
                                      effectiveStatus !== license.status
                                        ? `DB Status: ${license.status}, Computed: ${effectiveStatus} (based on dates)`
                                        : 'License status based on dates'
                                    }
                                  >
                                    {effectiveStatus}
                                  </span>
                                  {isDeleted && (
                                    <span
                                      className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700'
                                      title='Soft-deleted: hidden from the default view. Stripe subscription_id and instance_metadata were cleared.'
                                    >
                                      Deleted
                                    </span>
                                  )}
                                  {license.is_billing_period && (
                                    <span
                                      className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700'
                                      title='This license defines the billing period'
                                    >
                                      Billing Period
                                    </span>
                                  )}
                                  {license.billing_model === 'custom' && (
                                    <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700'>
                                      Enterprise
                                    </span>
                                  )}
                                  {license.manual_recurring && (
                                    <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700'>
                                      Recurring:{' '}
                                      {license.renewal_interval_months === 1
                                        ? 'Monthly'
                                        : license.renewal_interval_months === 3
                                          ? 'Quarterly'
                                          : license.renewal_interval_months === 6
                                            ? 'Semi-Annual'
                                            : license.renewal_interval_months === 12
                                              ? 'Yearly'
                                              : 'Monthly'}
                                    </span>
                                  )}
                                </div>
                                {/* Row 2: Stripe status (if linked) */}
                                {license.subscription_id && (
                                  <div className='flex items-center gap-1.5 text-xs'>
                                    <span className='text-gray-400'>Stripe:</span>
                                    {(() => {
                                      const subStatus = (
                                        license.instance_metadata as Record<string, unknown>
                                      )?.subscription_status as string | undefined
                                      const cancelAtPeriodEnd = (
                                        license.instance_metadata as Record<string, unknown>
                                      )?.cancel_at_period_end as boolean | undefined
                                      return (
                                        <div className='flex items-center gap-1'>
                                          <Chip
                                            size='sm'
                                            variant='flat'
                                            color={getSubscriptionStatusColor(subStatus)}
                                            className='text-xs h-5'
                                          >
                                            {subStatus || 'linked'}
                                          </Chip>
                                          {cancelAtPeriodEnd && (
                                            <span
                                              className='text-orange-600 text-xs'
                                              title='Subscription will cancel at period end'
                                            >
                                              (canceling)
                                            </span>
                                          )}
                                          {!cancelAtPeriodEnd && license.is_billing_period && (
                                            <span
                                              className='text-blue-600 text-xs'
                                              title='Subscription will auto-renew'
                                            >
                                              (renews)
                                            </span>
                                          )}
                                        </div>
                                      )
                                    })()}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className='flex flex-1 justify-end gap-1 whitespace-nowrap'>
                              <Button
                                variant='borderless'
                                className='h-7 text-xs px-2'
                                onClick={() => setSelectedLicenseDetails(license)}
                              >
                                Detail
                              </Button>
                              <Button
                                variant='borderless'
                                className='h-7 text-xs px-2'
                                onClick={() => handleEditLicense(license)}
                                isDisabled={editingLicense !== null || isDeleted}
                              >
                                Edit
                              </Button>
                              {effectiveStatus === 'active' && !isDeleted && (
                                <>
                                  <Button
                                    variant='borderless'
                                    className='h-7 text-xs px-2 text-orange-600 hover:bg-orange-50'
                                    onClick={() => setLicenseToRevoke(license)}
                                    isDisabled={editingLicense !== null}
                                  >
                                    Revoke
                                  </Button>
                                  <Button
                                    variant='destructive'
                                    className='h-7 text-xs px-2 bg-transparent hover:bg-red-50 border-none text-red-600'
                                    onClick={() => handleDeleteLicenseClick(license, false)}
                                    isDisabled={editingLicense !== null}
                                  >
                                    Delete
                                  </Button>
                                </>
                              )}
                              {license.status === 'pending' && (
                                <Button
                                  variant='destructive'
                                  className='h-7 text-xs px-2 bg-transparent hover:bg-red-50 border-none text-red-600'
                                  onClick={() => handleCancelPendingLicense(license.id)}
                                  isDisabled={editingLicense !== null}
                                >
                                  Cancel
                                </Button>
                              )}
                              {isDeleted && (
                                <Button
                                  variant='borderless'
                                  className='h-7 text-xs px-2 text-green-700 hover:bg-green-50'
                                  onClick={() => handleRestoreLicense(license)}
                                  isDisabled={editingLicense !== null || isRestoring === license.id}
                                >
                                  {isRestoring === license.id ? 'Restoring…' : 'Restore'}
                                </Button>
                              )}
                              {effectiveStatus === 'inactive' && (
                                <Button
                                  variant='destructive'
                                  className='h-7 text-xs px-2 bg-transparent hover:bg-red-50 border-none text-red-600'
                                  onClick={() => handleDeleteLicenseClick(license, true)}
                                  isDisabled={editingLicense !== null}
                                >
                                  Hard Delete
                                </Button>
                              )}
                            </div>
                          </div>
                        )
                      })
                  )}
                </div>
              </>
            )
          })()}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant='outlined' onClick={handleModalClose}>
          Close
        </Button>
      </ModalFooter>

      {/* Stripe Subscription Preview Modal */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false)
          setPreviewData(null)
        }}
        hideCloseButton={false}
        className='w-[550px]'
        overlayClassName='z-[70]'
      >
        <ModalBody className='p-5'>
          <h3 className='text-base font-semibold mb-4'>Stripe Subscription Preview</h3>

          {previewData && (
            <div className='space-y-4'>
              {/* Status Badge */}
              <div className='flex items-center gap-3'>
                <span className='text-sm font-medium text-gray-500'>Status:</span>
                <Chip
                  size='sm'
                  variant='flat'
                  color={getSubscriptionStatusColor(previewData.status)}
                >
                  {previewData.status || 'Unknown'}
                </Chip>
                {previewData.cancel_at_period_end && (
                  <Chip size='sm' variant='flat' color='warning'>
                    Cancels at period end
                  </Chip>
                )}
              </div>

              {/* Customer Info */}
              {(previewData.customer_email || previewData.customer_id) && (
                <div className='p-3 bg-gray-50 rounded-lg'>
                  <div className='text-xs font-medium text-gray-500 mb-2'>Customer</div>
                  {previewData.customer_email && (
                    <div className='text-sm'>{previewData.customer_email}</div>
                  )}
                  {previewData.customer_id && (
                    <div className='text-xs text-gray-400 font-mono'>{previewData.customer_id}</div>
                  )}
                </div>
              )}

              {/* Plan Detection */}
              <div className='p-3 bg-blue-50 rounded-lg border border-blue-100'>
                <div className='text-xs font-medium text-blue-600 mb-2'>Detected Plan Tier</div>
                {previewData.license_type_name ? (
                  <div className='flex items-center gap-2'>
                    <span className='text-sm font-medium'>
                      {previewData.license_type_label || previewData.license_type_name}
                    </span>
                    <span className='text-xs text-gray-500'>({previewData.license_type_name})</span>
                  </div>
                ) : (
                  <div className='text-sm text-orange-600'>
                    No plan tier detected - will use selected license type
                  </div>
                )}
                {previewData.product_name && (
                  <div className='text-xs text-gray-500 mt-1'>
                    Product: {previewData.product_name}
                  </div>
                )}
              </div>

              {/* Billing Period */}
              <div className='grid grid-cols-2 gap-3'>
                <div className='p-3 bg-gray-50 rounded-lg'>
                  <div className='text-xs font-medium text-gray-500 mb-1'>Start Date</div>
                  <div className='text-sm'>
                    {previewData.start_date ? formatDate(previewData.start_date) : 'N/A'}
                  </div>
                </div>
                <div className='p-3 bg-gray-50 rounded-lg'>
                  <div className='text-xs font-medium text-gray-500 mb-1'>End Date</div>
                  <div className='text-sm'>
                    {previewData.end_date ? formatDate(previewData.end_date) : 'N/A'}
                  </div>
                  {previewData.cancel_at && (
                    <div className='text-xs text-orange-600 mt-1'>
                      Cancels: {formatDate(previewData.cancel_at)}
                    </div>
                  )}
                </div>
              </div>

              {/* Pricing */}
              {(previewData.total_amount != null || previewData.price_amount != null) && (
                <div className='p-3 bg-gray-50 rounded-lg'>
                  <div className='text-xs font-medium text-gray-500 mb-1'>Price</div>
                  <div className='text-sm'>
                    {/* Show total price */}
                    <span className='font-medium'>
                      {formatCurrency(
                        previewData.total_amount ?? previewData.price_amount,
                        previewData.price_currency
                      )}
                    </span>
                    {previewData.price_interval && (
                      <span className='text-gray-500'> / {previewData.price_interval}</span>
                    )}
                    {/* Show breakdown if there's quantity > 1 */}
                    {previewData.quantity != null &&
                      previewData.quantity > 1 &&
                      previewData.price_amount != null && (
                        <div className='text-xs text-gray-500 mt-1'>
                          ({previewData.quantity} ×{' '}
                          {formatCurrency(previewData.price_amount, previewData.price_currency)})
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Subscription ID */}
              <div className='p-3 bg-gray-50 rounded-lg'>
                <div className='text-xs font-medium text-gray-500 mb-1'>Subscription ID</div>
                <div className='text-sm font-mono text-gray-700'>{previewData.subscription_id}</div>
              </div>

              {/* Warning */}
              <div className='p-3 bg-yellow-50 rounded-lg border border-yellow-200'>
                <div className='text-xs text-yellow-800'>
                  <strong>Note:</strong> Applying this subscription will make the license read-only.
                  All fields will sync from Stripe. You can unlink later to regain manual control.
                </div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant='outlined'
            onClick={() => {
              setShowPreviewModal(false)
              setPreviewData(null)
            }}
          >
            Cancel
          </Button>
          <Button variant='primary' onClick={handleConfirmApplySubscription} isDisabled={isSyncing}>
            {isSyncing ? 'Applying...' : 'Confirm & Apply'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Add License Modal */}
      <Modal
        isOpen={showAddForm}
        onClose={() => {
          setShowAddForm(false)
          setShowPaymentForm(false)
          setPaymentLinkResult(null)
          setNewSubscriptionId('')
          setIsNewLicenseStripeLinked(false)
        }}
        hideCloseButton={false}
        className='w-[650px] max-h-[90vh]'
        overlayClassName='z-[60]'
      >
        <ModalBody className='p-5 overflow-y-auto'>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-base font-semibold'>Add New License</h3>
            {isNewLicenseStripeLinked && (
              <span className='text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded'>
                Stripe linked
              </span>
            )}
          </div>

          <div className='space-y-4'>
            {/* Subscription ID - Primary control for Stripe-managed licenses */}
            <div className='p-3 bg-gray-50 border border-gray-200 rounded-lg'>
              <label className='text-sm font-medium text-gray-700 mb-1 block'>
                Stripe Subscription ID
                {isNewLicenseStripeLinked && (
                  <span className='ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded'>
                    Stripe Managed
                  </span>
                )}
              </label>
              <div className='flex gap-2'>
                <input
                  type='text'
                  className='flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono'
                  value={newSubscriptionId}
                  onChange={e => setNewSubscriptionId(e.target.value)}
                  placeholder='sub_xxxxx (leave empty for manual control)'
                />
                <Button
                  variant='primary'
                  onClick={() => {
                    if (!newSubscriptionId) {
                      // Clear Stripe linking
                      setIsNewLicenseStripeLinked(false)
                      BetterToast.success('Manual control enabled')
                      return
                    }
                    // Show preview before applying
                    handlePreviewSubscription(newSubscriptionId, 'add')
                  }}
                  isDisabled={isSyncing || isLoadingPreview}
                  className='h-[38px] px-4 text-sm whitespace-nowrap'
                >
                  {isLoadingPreview ? 'Loading...' : isSyncing ? 'Applying...' : 'Preview'}
                </Button>
              </div>
              <p className='text-xs text-gray-500 mt-1'>
                {isNewLicenseStripeLinked
                  ? 'All fields below are read-only and synced from Stripe. Clear to enable manual control.'
                  : 'Enter a Stripe subscription ID and click Preview to see what will be synced.'}
              </p>
            </div>

            {/* License Type */}
            <div>
              <label className='text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5'>
                License Type
                {isNewLicenseStripeLinked && (
                  <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                )}
                <span className='inline-flex h-4 items-center gap-1 text-[11px] font-medium text-tokens-neutral-text-tertiary'>
                  ⚡ = billable flow
                </span>
              </label>
              <select
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${isNewLicenseStripeLinked ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
                value={selectedLicenseType || ''}
                onChange={e => handleLicenseTypeChange(Number(e.target.value))}
                disabled={isNewLicenseStripeLinked}
              >
                {licenseTypes.map(lt => (
                  <option key={lt.id} value={lt.id}>
                    {lt.label} ({lt.name}){isBillableFlowLicense(lt.name) ? ' ⚡' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Period */}
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-sm font-medium text-gray-700 mb-1 block'>
                  Start Date
                  {isNewLicenseStripeLinked && (
                    <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                  )}
                </label>
                <input
                  type='datetime-local'
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${isNewLicenseStripeLinked ? 'bg-gray-50 text-gray-500' : ''}`}
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  disabled={isNewLicenseStripeLinked}
                />
              </div>
              <div>
                <label className='text-sm font-medium text-gray-700 mb-1 block'>
                  End Date
                  {isNewLicenseStripeLinked && (
                    <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                  )}
                </label>
                <input
                  type='datetime-local'
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${isNewLicenseStripeLinked ? 'bg-gray-50 text-gray-500' : ''}`}
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  min={startDate}
                  disabled={isNewLicenseStripeLinked}
                />
              </div>
            </div>

            {/* Billing Type */}
            <div>
              <label className='text-sm font-medium text-gray-700 mb-1 block'>
                Billing Type
                {isNewLicenseStripeLinked && (
                  <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                )}
              </label>
              <div className='flex gap-2'>
                <label
                  className={`flex-1 flex items-center gap-2 p-2.5 border rounded-lg text-sm ${
                    newBillingType === 'quota' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  } ${isNewLicenseStripeLinked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-gray-300'}`}
                >
                  <input
                    type='radio'
                    name='addBillingType'
                    checked={newBillingType === 'quota'}
                    onChange={() => handleBillingTypeChange('quota')}
                    className='w-3.5 h-3.5 text-blue-600'
                    disabled={isNewLicenseStripeLinked}
                  />
                  <span>Credit-based</span>
                </label>
                <label
                  className={`flex-1 flex items-center gap-2 p-2.5 border rounded-lg text-sm ${
                    newBillingType === 'billable_flow'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  } ${isNewLicenseStripeLinked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-gray-300'}`}
                >
                  <input
                    type='radio'
                    name='addBillingType'
                    checked={newBillingType === 'billable_flow'}
                    onChange={() => handleBillingTypeChange('billable_flow')}
                    className='w-3.5 h-3.5 text-blue-600'
                    disabled={isNewLicenseStripeLinked}
                  />
                  <span>Flow-based</span>
                </label>
              </div>
            </div>

            {/* Quota Input */}
            {newBillingType === 'quota' && (
              <div>
                <label className='text-sm font-medium text-gray-700 mb-1 block'>
                  Credit Quota
                  {isNewLicenseStripeLinked && (
                    <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                  )}
                </label>
                <div className='flex items-center gap-2'>
                  <input
                    type='number'
                    className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50 disabled:text-gray-400 ${isNewLicenseStripeLinked ? 'bg-gray-50 text-gray-500' : ''}`}
                    value={newUnlimitedQuota ? '' : newLicenseQuota}
                    onChange={e => setNewLicenseQuota(e.target.value)}
                    placeholder={newUnlimitedQuota ? 'Unlimited' : '0'}
                    disabled={newUnlimitedQuota || isNewLicenseStripeLinked}
                  />
                  <label
                    className={`flex items-center gap-1.5 text-sm text-gray-600 ${isNewLicenseStripeLinked ? 'opacity-60' : ''}`}
                  >
                    <input
                      type='checkbox'
                      checked={newUnlimitedQuota}
                      onChange={e => setNewUnlimitedQuota(e.target.checked)}
                      className='w-3.5 h-3.5'
                      disabled={isNewLicenseStripeLinked}
                    />
                    Unlimited
                  </label>
                </div>
              </div>
            )}

            {newBillingType === 'billable_flow' && (
              <div>
                <label className='text-sm font-medium text-gray-700 mb-1 block'>
                  Billable Flows
                  {isNewLicenseStripeLinked && (
                    <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                  )}
                </label>
                <div className='flex items-center gap-2'>
                  <input
                    type='number'
                    className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50 disabled:text-gray-400 ${isNewLicenseStripeLinked ? 'bg-gray-50 text-gray-500' : ''}`}
                    value={newUnlimitedBillableFlows ? '' : newBillableFlowsQuota}
                    onChange={e => setNewBillableFlowsQuota(e.target.value)}
                    placeholder={newUnlimitedBillableFlows ? 'Unlimited' : '0'}
                    disabled={newUnlimitedBillableFlows || isNewLicenseStripeLinked}
                  />
                  <label
                    className={`flex items-center gap-1.5 text-sm text-gray-600 ${isNewLicenseStripeLinked ? 'opacity-60' : ''}`}
                  >
                    <input
                      type='checkbox'
                      checked={newUnlimitedBillableFlows}
                      onChange={e => setNewUnlimitedBillableFlows(e.target.checked)}
                      className='w-3.5 h-3.5'
                      disabled={isNewLicenseStripeLinked}
                    />
                    Unlimited
                  </label>
                </div>
              </div>
            )}

            {/* Billing Package - applies when generating Stripe payment links */}
            {!isNewLicenseStripeLinked &&
              (() => {
                const selectedType = licenseTypes.find(lt => lt.id === selectedLicenseType)
                const billingModel = selectedType?.billing_model || 'prepaid'
                const isStripeEnabled =
                  billingModel === 'subscription' || billingModel === 'per_seat'
                if (!isStripeEnabled) return null

                return (
                  <div>
                    <label
                      htmlFor='payment-billing-package'
                      className='text-sm font-medium text-gray-700 mb-1 block'
                    >
                      Billing Package
                    </label>
                    <select
                      id='payment-billing-package'
                      className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'
                      value={paymentBillingPackage}
                      onChange={e =>
                        setPaymentBillingPackage(e.target.value as 'monthly' | 'six_month')
                      }
                    >
                      <option value='monthly'>Monthly</option>
                      <option value='six_month'>6 months</option>
                    </select>
                    <p className='mt-1 text-xs text-gray-500'>
                      This controls the Stripe price used for the payment link. 6-month access still
                      uses monthly included-credit refreshes.
                    </p>
                  </div>
                )
              })()}

            {/* Payment Link Section - Hidden when Stripe linked */}
            {!isNewLicenseStripeLinked &&
              (() => {
                const selectedType = licenseTypes.find(lt => lt.id === selectedLicenseType)
                const billingModel = selectedType?.billing_model || 'prepaid'
                const isStripeEnabled =
                  billingModel === 'subscription' || billingModel === 'per_seat'
                if (!isStripeEnabled) return null

                return showPaymentForm ? (
                  <div className='p-3 bg-purple-50 border border-purple-200 rounded-lg'>
                    <div className='flex items-center justify-between mb-2'>
                      <span className='text-sm font-medium text-purple-800'>Payment Link</span>
                      <button
                        onClick={() => setShowPaymentForm(false)}
                        className='text-xs text-purple-600 hover:underline'
                      >
                        Cancel
                      </button>
                    </div>
                    <div className='mb-2'>
                      <label className='text-xs font-medium text-gray-600 mb-1 block'>
                        Contract End Date (optional)
                      </label>
                      <input
                        type='datetime-local'
                        className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm'
                        value={paymentContractEndDate}
                        onChange={e => setPaymentContractEndDate(e.target.value)}
                      />
                    </div>
                    <Button
                      variant='primary'
                      onClick={handleCreateWithPayment}
                      isDisabled={isCreatingPaymentLink || !selectedLicenseType}
                      className='h-8 px-3 text-sm bg-purple-600 hover:bg-purple-700 w-full'
                    >
                      {isCreatingPaymentLink ? 'Creating...' : 'Generate Payment Link'}
                    </Button>
                    {paymentLinkResult && (
                      <div className='mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs'>
                        <p className='font-medium text-green-800 mb-1'>Link created!</p>
                        <div className='flex gap-1'>
                          <input
                            type='text'
                            readOnly
                            value={paymentLinkResult.payment_url}
                            className='flex-1 px-2 py-1 border rounded text-xs'
                          />
                          <Button
                            variant='outlined'
                            onClick={() => {
                              navigator.clipboard.writeText(paymentLinkResult.payment_url)
                              BetterToast.success('Copied!')
                            }}
                            className='h-6 px-2 text-xs'
                          >
                            Copy
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setShowPaymentForm(true)}
                    className='text-sm text-purple-600 hover:underline'
                  >
                    + Create with payment link
                  </button>
                )
              })()}

            {/* Enterprise Section - Hidden when Stripe linked */}
            {!isNewLicenseStripeLinked &&
              (() => {
                const selectedType = licenseTypes.find(lt => lt.id === selectedLicenseType)
                const isEnterprise = selectedType?.billing_model === 'custom'
                if (!isEnterprise) return null

                return (
                  <div className='p-3 bg-orange-50 border border-orange-200 rounded-lg'>
                    <div className='text-sm font-medium text-orange-800 mb-2'>
                      Enterprise Billing
                    </div>
                    <div className='grid grid-cols-3 gap-2'>
                      <div>
                        <label className='text-xs font-medium text-gray-600 mb-1 block'>
                          Value ($)
                        </label>
                        <input
                          type='number'
                          className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm'
                          placeholder='10000'
                          value={contractValueDollars}
                          onChange={e => setContractValueDollars(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className='text-xs font-medium text-gray-600 mb-1 block'>
                          Schedule
                        </label>
                        <select
                          className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm'
                          value={billingSchedule}
                          onChange={e => setBillingSchedule(e.target.value)}
                        >
                          <option value='monthly'>Monthly</option>
                          <option value='quarterly'>Quarterly</option>
                          <option value='annually'>Annually</option>
                        </select>
                      </div>
                      <div>
                        <label className='text-xs font-medium text-gray-600 mb-1 block'>
                          Terms
                        </label>
                        <select
                          className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm'
                          value={paymentTermsDays}
                          onChange={e => setPaymentTermsDays(e.target.value)}
                        >
                          <option value='15'>Net 15</option>
                          <option value='30'>Net 30</option>
                          <option value='60'>Net 60</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )
              })()}

            {/* Manual Recurring - Only visible when no Stripe subscription */}
            {!isNewLicenseStripeLinked && !newSubscriptionId && (
              <div className='p-3 bg-indigo-50 border border-indigo-200 rounded-lg'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='checkbox'
                    className='rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                    checked={newManualRecurring}
                    onChange={e => setNewManualRecurring(e.target.checked)}
                  />
                  <span className='text-sm font-medium text-indigo-800'>Manual Recurring</span>
                  <span className='text-xs text-indigo-600'>Auto-renew without Stripe</span>
                </label>
                {newManualRecurring && (
                  <div className='mt-3 grid grid-cols-2 gap-3'>
                    <div>
                      <label className='text-xs font-medium text-gray-600 mb-1 block'>
                        Renewal Interval
                      </label>
                      <select
                        className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white'
                        value={newRenewalInterval}
                        onChange={e => setNewRenewalInterval(Number(e.target.value))}
                      >
                        <option value={1}>Monthly</option>
                        <option value={3}>Quarterly</option>
                        <option value={6}>Semi-Annual</option>
                        <option value={12}>Yearly</option>
                      </select>
                    </div>
                    <div>
                      <label className='text-xs font-medium text-gray-600 mb-1 block'>
                        Recurring End Date <span className='text-red-500'>*</span>
                      </label>
                      <input
                        type='datetime-local'
                        className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm'
                        value={newRecurringEndDate}
                        onChange={e => setNewRecurringEndDate(e.target.value)}
                        min={endDate}
                      />
                    </div>
                  </div>
                )}
                {newManualRecurring && (
                  <p className='text-xs text-indigo-600 mt-2'>
                    License will auto-renew every{' '}
                    {newRenewalInterval === 1
                      ? 'month'
                      : newRenewalInterval === 3
                        ? '3 months'
                        : newRenewalInterval === 6
                          ? '6 months'
                          : 'year'}{' '}
                    until recurring end date. Credits reset each cycle.
                  </p>
                )}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className='px-5 py-3 border-t'>
          <div className='flex items-center justify-between w-full'>
            {isNewLicenseStripeLinked && (
              <span className='text-xs text-gray-500'>
                License created from Stripe subscription.
              </span>
            )}
            <div className='flex gap-2 ml-auto'>
              <Button
                variant='outlined'
                onClick={() => {
                  setShowAddForm(false)
                  setShowPaymentForm(false)
                  setPaymentLinkResult(null)
                  setNewSubscriptionId('')
                  setIsNewLicenseStripeLinked(false)
                }}
                className='h-8 px-4 text-sm'
              >
                {isNewLicenseStripeLinked ? 'Close' : 'Cancel'}
              </Button>
              {!isNewLicenseStripeLinked && (
                <Button
                  variant='primary'
                  onClick={handleAddLicense}
                  isDisabled={isAdding || !selectedLicenseType}
                  className='h-8 px-4 text-sm'
                >
                  {isAdding ? 'Adding...' : 'Add License'}
                </Button>
              )}
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Edit License Modal */}
      <Modal
        isOpen={editingLicense !== null}
        onClose={() => {
          setEditingLicense(null)
        }}
        hideCloseButton={false}
        className='w-[650px] max-h-[90vh]'
        overlayClassName='z-[60]'
      >
        <ModalBody className='p-5 overflow-y-auto'>
          <div className='flex items-center justify-between mb-4'>
            <div>
              <Typography
                fontFamily='geist'
                fontSize='bodyLgMedium'
                fontWeight='semibold'
                className='text-tokens-neutral-text-primary select-none'
              >
                Edit License{' '}
                <span className='text-tokens-neutral-text-tertiary'>#{editingLicense?.id}</span>
              </Typography>
              {editingLicense?.subscription_id && (
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='text-tokens-neutral-text-secondary'
                >
                  Stripe-linked licenses are synced from Stripe. Unlink to enable manual edits.
                </Typography>
              )}
            </div>
            {editingLicense?.subscription_id && (
              <div className='flex items-center gap-2'>
                <span className='rounded-full bg-tokens-neutral-fill-fill-6 px-2.5 py-1 text-xs font-medium text-tokens-neutral-text-secondary'>
                  {formatBillingPackageLabel(getLicenseBillingPackage(editingLicense))}
                </span>
                <span className='rounded-full bg-tokens-status-link-base px-2.5 py-1 text-xs font-medium text-tokens-status-link-2'>
                  Stripe linked
                </span>
              </div>
            )}
          </div>

          <div className='space-y-4'>
            {/* Subscription ID - Primary control for Stripe-managed licenses */}
            <div className='rounded-[14px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white p-4 shadow-segment'>
              <label className='mb-2 block text-sm font-medium text-tokens-neutral-text-primary'>
                Stripe Subscription ID{' '}
                {editingLicense?.subscription_id && (
                  <span className='ml-2 rounded-full bg-tokens-status-link-base px-2 py-0.5 text-xs font-medium text-tokens-status-link-2'>
                    Stripe Managed
                  </span>
                )}
              </label>
              <div className='flex gap-2'>
                <input
                  type='text'
                  className={`flex-1 rounded-[10px] border border-tokens-neutral-border-2 px-3 py-2 text-sm font-mono shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] ${
                    editingLicense?.subscription_id
                      ? 'cursor-not-allowed bg-tokens-neutral-bg-grey-1 text-tokens-neutral-text-tertiary'
                      : 'bg-tokens-neutral-bg-white text-tokens-neutral-text-primary focus:border-tokens-status-link-2 focus:outline-none focus:ring-2 focus:ring-tokens-status-link-base'
                  }`}
                  value={editForm.subscription_id}
                  onChange={e => setEditForm({ ...editForm, subscription_id: e.target.value })}
                  placeholder='sub_xxxxx (leave empty for manual control)'
                  disabled={!!editingLicense?.subscription_id}
                />
                <Button
                  variant='primary'
                  onClick={() => {
                    if (!editForm.subscription_id) {
                      // Clear handled by Unlink button
                      return
                    }
                    // Show preview before applying
                    handlePreviewSubscription(editForm.subscription_id, 'edit')
                  }}
                  isDisabled={
                    isSyncing ||
                    isLoadingPreview ||
                    !editForm.subscription_id ||
                    editForm.subscription_id === (editingLicense?.subscription_id || '')
                  }
                  className='h-[38px] px-4 text-sm whitespace-nowrap'
                >
                  {isLoadingPreview ? 'Loading...' : isSyncing ? 'Applying...' : 'Preview'}
                </Button>
                {/* Unlink button - only show if currently linked */}
                {editingLicense?.subscription_id && (
                  <Button
                    variant='outlined'
                    onClick={() => handleUnlinkSubscription(editingLicense.id)}
                    isDisabled={isSyncing}
                    className='h-[38px] px-4 text-sm whitespace-nowrap text-orange-600 border-orange-300 hover:bg-orange-50'
                  >
                    Unlink
                  </Button>
                )}
              </div>
              <p className='mt-2 text-xs text-tokens-neutral-text-secondary'>
                {editingLicense?.subscription_id
                  ? 'Subscription ID is locked while linked. Click "Unlink" to disconnect from Stripe and enable manual control.'
                  : 'Enter a Stripe subscription ID and click Preview to see what will be synced.'}
              </p>
            </div>

            {editForm.billing_type !== 'billable_flow' && (
              <div className='rounded-[14px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white p-4 shadow-segment'>
                <div className='mb-3 flex items-start justify-between gap-4'>
                  <div className='flex items-center gap-2'>
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyMdMedium'
                      fontWeight='semibold'
                      className='text-tokens-neutral-text-primary select-none'
                    >
                      {editingLicense?.subscription_id ? 'Stripe Billing Cycle' : 'Credit Refresh'}
                    </Typography>
                    {editingLicense?.subscription_id && <Tag variant='info'>Stripe controlled</Tag>}
                  </div>
                  <select
                    className={`h-9 min-w-[200px] rounded-full border border-tokens-neutral-border-2 px-3 text-sm shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      editingLicense?.subscription_id
                        ? 'cursor-not-allowed bg-tokens-neutral-bg-grey-1 text-tokens-neutral-text-tertiary'
                        : 'cursor-pointer bg-tokens-neutral-bg-white text-tokens-neutral-text-primary'
                    }`}
                    value={editForm.billing_package}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        billing_package: e.target.value as 'monthly' | 'six_month' | 'none'
                      })
                    }
                    disabled={!!editingLicense?.subscription_id}
                  >
                    {editingLicense?.subscription_id ? (
                      <>
                        <option value='monthly'>Monthly</option>
                        <option value='six_month'>6 months</option>
                      </>
                    ) : (
                      <>
                        <option value='monthly'>Refresh monthly</option>
                        <option value='none'>Single window (no refresh)</option>
                      </>
                    )}
                  </select>
                </div>
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  className='mb-3 text-tokens-neutral-text-secondary'
                >
                  {editingLicense?.subscription_id
                    ? 'Synced from the Stripe subscription. Unlink before changing.'
                    : 'When does the credit pool reset inside the access window?'}
                </Typography>
                <div className='grid grid-cols-2 gap-3'>
                  <div className='rounded-[12px] bg-tokens-neutral-bg-grey-1 p-3'>
                    <div className='text-[11px] font-medium uppercase tracking-[0.04em] text-tokens-neutral-text-tertiary'>
                      {editingLicense?.subscription_id ? 'Current package' : 'Current policy'}
                    </div>
                    <div className='mt-1 text-sm font-semibold text-tokens-neutral-text-primary'>
                      {editingLicense?.subscription_id
                        ? formatBillingPackageLabel(getLicenseBillingPackage(editingLicense))
                        : getLicenseBillingPackage(editingLicense) === 'none'
                          ? 'Single window'
                          : 'Monthly refresh'}
                    </div>
                  </div>
                  <div className='rounded-[12px] bg-tokens-neutral-bg-grey-1 p-3'>
                    <div className='text-[11px] font-medium uppercase tracking-[0.04em] text-tokens-neutral-text-tertiary'>
                      Billing period
                    </div>
                    <div className='mt-1 text-sm font-semibold text-tokens-neutral-text-primary'>
                      {editForm.billing_package === 'none'
                        ? 'Whole access window'
                        : 'Refreshes monthly'}
                    </div>
                    {editingLicense &&
                      (editingLicense.credit_period_start || editingLicense.credit_period_end) && (
                        <div className='mt-1 text-xs text-tokens-neutral-text-secondary'>
                          {editingLicense.credit_period_start
                            ? formatDate(editingLicense.credit_period_start)
                            : 'N/A'}{' '}
                          to{' '}
                          {editingLicense.credit_period_end
                            ? formatDate(editingLicense.credit_period_end)
                            : 'N/A'}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* License Type */}
            <div>
              <label className='text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5'>
                License Type
                {editingLicense?.subscription_id && (
                  <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                )}
                <span className='inline-flex h-4 items-center gap-1 text-[11px] font-medium text-tokens-neutral-text-tertiary'>
                  ⚡ = billable flow
                </span>
              </label>
              <select
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${editingLicense?.subscription_id ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
                value={editForm.license_type_id}
                onChange={e => {
                  const newTypeId = Number(e.target.value)
                  const newType = licenseTypes.find(lt => lt.id === newTypeId)
                  const isEnterpriseMigrateType =
                    newType?.name?.startsWith('enterprise-migrate') ?? false
                  setEditForm({
                    ...editForm,
                    license_type_id: newTypeId,
                    quota: isEnterpriseMigrateType
                      ? ''
                      : newType?.quota != null && newType.quota > 0 && !isUnlimited(newType.quota)
                        ? newType.quota.toString()
                        : '0',
                    billable_flows_quota: isEnterpriseMigrateType
                      ? ''
                      : newType?.billable_flows_limit != null &&
                          newType.billable_flows_limit > 0 &&
                          !isUnlimited(newType.billable_flows_limit)
                        ? newType.billable_flows_limit.toString()
                        : '0',
                    unlimited_quota: isEnterpriseMigrateType,
                    unlimited_billable_flows: isEnterpriseMigrateType,
                    billing_type: isEnterpriseMigrateType ? 'billable_flow' : 'quota'
                  })
                }}
                disabled={!!editingLicense?.subscription_id}
              >
                {licenseTypes.map(lt => (
                  <option key={lt.id} value={lt.id}>
                    {lt.label} ({lt.name}){isBillableFlowLicense(lt.name) ? ' ⚡' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Period */}
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-sm font-medium text-gray-700 mb-1 block'>
                  Start Date
                  {editingLicense?.subscription_id && (
                    <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                  )}
                </label>
                <input
                  type='datetime-local'
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm ${editingLicense?.subscription_id ? 'bg-gray-50 text-gray-500' : ''}`}
                  value={editForm.start_date}
                  onChange={e => setEditForm({ ...editForm, start_date: e.target.value })}
                  disabled={!!editingLicense?.subscription_id}
                />
              </div>
              <div>
                <label className='text-sm font-medium text-gray-700 mb-1 block'>
                  End Date
                  {editingLicense?.subscription_id && (
                    <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                  )}
                </label>
                <input
                  type='datetime-local'
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm ${editingLicense?.subscription_id ? 'bg-gray-50 text-gray-500' : ''}`}
                  value={editForm.end_date}
                  onChange={e => setEditForm({ ...editForm, end_date: e.target.value })}
                  min={editForm.start_date}
                  disabled={!!editingLicense?.subscription_id}
                />
              </div>
            </div>

            {/* Contract End Date - Only for Stripe-subscribed licenses */}
            {editingLicense?.subscription_id && (
              <div>
                <label className='text-sm font-medium text-gray-700 mb-1 block'>
                  Contract End Date <span className='text-gray-400 font-normal'>(optional)</span>
                  <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                </label>
                <div className='flex items-center gap-2'>
                  <input
                    type='datetime-local'
                    className='flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-500'
                    value={editForm.contract_end_date}
                    onChange={e => setEditForm({ ...editForm, contract_end_date: e.target.value })}
                    min={editForm.start_date}
                    disabled
                  />
                </div>
              </div>
            )}

            {/* Manual Recurring - Only visible when no Stripe subscription */}
            {!editingLicense?.subscription_id && (
              <div className='p-3 bg-indigo-50 border border-indigo-200 rounded-lg'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                    type='checkbox'
                    className='rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                    checked={editForm.manual_recurring}
                    onChange={e => setEditForm({ ...editForm, manual_recurring: e.target.checked })}
                  />
                  <span className='text-sm font-medium text-indigo-800'>Manual Recurring</span>
                  <span className='text-xs text-indigo-600'>Auto-renew without Stripe</span>
                </label>
                {editForm.manual_recurring && (
                  <div className='mt-3 grid grid-cols-2 gap-3'>
                    <div>
                      <label className='text-xs font-medium text-gray-600 mb-1 block'>
                        Renewal Interval
                      </label>
                      <select
                        className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm bg-white'
                        value={editForm.renewal_interval_months}
                        onChange={e =>
                          setEditForm({
                            ...editForm,
                            renewal_interval_months: Number(e.target.value)
                          })
                        }
                      >
                        <option value={1}>Monthly</option>
                        <option value={3}>Quarterly</option>
                        <option value={6}>Semi-Annual</option>
                        <option value={12}>Yearly</option>
                      </select>
                    </div>
                    <div>
                      <label className='text-xs font-medium text-gray-600 mb-1 block'>
                        Recurring End Date <span className='text-red-500'>*</span>
                      </label>
                      <input
                        type='datetime-local'
                        className='w-full px-2 py-1.5 border border-gray-300 rounded text-sm'
                        value={editForm.recurring_end_date}
                        onChange={e =>
                          setEditForm({ ...editForm, recurring_end_date: e.target.value })
                        }
                        min={editForm.end_date}
                      />
                    </div>
                  </div>
                )}
                {editForm.manual_recurring && (
                  <p className='text-xs text-indigo-600 mt-2'>
                    License will auto-renew every{' '}
                    {editForm.renewal_interval_months === 1
                      ? 'month'
                      : editForm.renewal_interval_months === 3
                        ? '3 months'
                        : editForm.renewal_interval_months === 6
                          ? '6 months'
                          : 'year'}{' '}
                    until recurring end date. Credits reset each cycle.
                  </p>
                )}
              </div>
            )}

            {/* Billing Type */}
            <div>
              <label className='text-sm font-medium text-gray-700 mb-1 block'>
                Billing Type
                {editingLicense?.subscription_id && (
                  <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                )}
              </label>
              <div className='flex gap-2'>
                <label
                  className={`flex-1 flex items-center gap-2 p-2.5 border rounded-lg text-sm ${
                    editForm.billing_type === 'quota'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  } ${editingLicense?.subscription_id ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-gray-300'}`}
                >
                  <input
                    type='radio'
                    name='editModalBillingType'
                    checked={editForm.billing_type === 'quota'}
                    onChange={() => {
                      const licenseType = licenseTypes.find(
                        lt => lt.id === editForm.license_type_id
                      )
                      setEditForm({
                        ...editForm,
                        billing_type: 'quota',
                        unlimited_quota: isUnlimited(licenseType?.quota) || false,
                        unlimited_billable_flows: false,
                        billable_flows_quota: '0'
                      })
                    }}
                    className='w-3.5 h-3.5 text-blue-600'
                    disabled={!!editingLicense?.subscription_id}
                  />
                  <span>Credit-based</span>
                </label>
                <label
                  className={`flex-1 flex items-center gap-2 p-2.5 border rounded-lg text-sm ${
                    editForm.billing_type === 'billable_flow'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  } ${editingLicense?.subscription_id ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-gray-300'}`}
                >
                  <input
                    type='radio'
                    name='editModalBillingType'
                    checked={editForm.billing_type === 'billable_flow'}
                    onChange={() =>
                      setEditForm({
                        ...editForm,
                        billing_type: 'billable_flow',
                        unlimited_quota: true,
                        quota: ''
                      })
                    }
                    className='w-3.5 h-3.5 text-blue-600'
                    disabled={!!editingLicense?.subscription_id}
                  />
                  <span>Flow-based</span>
                </label>
              </div>
            </div>

            {/* Quota Input */}
            {editForm.billing_type === 'quota' && (
              <div>
                <label className='text-sm font-medium text-gray-700 mb-1 block'>
                  Credit Quota
                  {editingLicense?.subscription_id && (
                    <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                  )}
                </label>
                <div className='flex items-center gap-2'>
                  <input
                    type='number'
                    className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50 disabled:text-gray-400 ${editingLicense?.subscription_id ? 'bg-gray-50 text-gray-500' : ''}`}
                    value={editForm.unlimited_quota ? '' : editForm.quota}
                    onChange={e => setEditForm({ ...editForm, quota: e.target.value })}
                    placeholder={editForm.unlimited_quota ? 'Unlimited' : '0'}
                    disabled={editForm.unlimited_quota || !!editingLicense?.subscription_id}
                  />
                  <label
                    className={`flex items-center gap-1.5 text-sm text-gray-600 ${editingLicense?.subscription_id ? 'opacity-60' : ''}`}
                  >
                    <input
                      type='checkbox'
                      checked={editForm.unlimited_quota}
                      onChange={e =>
                        setEditForm({ ...editForm, unlimited_quota: e.target.checked })
                      }
                      className='w-3.5 h-3.5'
                      disabled={!!editingLicense?.subscription_id}
                    />
                    Unlimited
                  </label>
                </div>
              </div>
            )}

            {editForm.billing_type === 'billable_flow' && (
              <div>
                <label className='text-sm font-medium text-gray-700 mb-1 block'>
                  Billable Flows
                  {editingLicense?.subscription_id && (
                    <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                  )}
                </label>
                <div className='flex items-center gap-2'>
                  <input
                    type='number'
                    className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50 disabled:text-gray-400 ${editingLicense?.subscription_id ? 'bg-gray-50 text-gray-500' : ''}`}
                    value={editForm.unlimited_billable_flows ? '' : editForm.billable_flows_quota}
                    onChange={e =>
                      setEditForm({ ...editForm, billable_flows_quota: e.target.value })
                    }
                    placeholder={editForm.unlimited_billable_flows ? 'Unlimited' : '0'}
                    disabled={
                      editForm.unlimited_billable_flows || !!editingLicense?.subscription_id
                    }
                  />
                  <label
                    className={`flex items-center gap-1.5 text-sm text-gray-600 ${editingLicense?.subscription_id ? 'opacity-60' : ''}`}
                  >
                    <input
                      type='checkbox'
                      checked={editForm.unlimited_billable_flows}
                      onChange={e =>
                        setEditForm({ ...editForm, unlimited_billable_flows: e.target.checked })
                      }
                      className='w-3.5 h-3.5'
                      disabled={!!editingLicense?.subscription_id}
                    />
                    Unlimited
                  </label>
                </div>
              </div>
            )}

            {/* Status indicators. The Billing Period chip lives on the row badge
                — derived from tier hierarchy on the response, never editable here. */}
            {editingLicense?.subscription_id && (
              <div className='flex items-center gap-4 text-sm'>
                <span
                  className={`flex items-center gap-1.5 ${editForm.auto_renew ? 'text-green-600' : 'text-amber-600'}`}
                >
                  {editForm.auto_renew ? '✓ Auto Renew' : '⏸ No Auto Renew'}
                  <span className='text-xs text-blue-600'>(Stripe)</span>
                </span>
              </div>
            )}

            {/* Enterprise Section */}
            {(() => {
              const editLicenseType = licenseTypes.find(lt => lt.id === editForm.license_type_id)
              const isEnterprise = editLicenseType?.billing_model === 'custom'
              if (!isEnterprise) return null

              const isStripeManaged = !!editingLicense?.subscription_id
              return (
                <div
                  className={`p-3 border rounded-lg ${isStripeManaged ? 'bg-purple-50/50 border-purple-200' : 'bg-purple-50 border-purple-200'}`}
                >
                  <div className='text-sm font-medium text-purple-800 mb-2'>
                    Enterprise Billing
                    {isStripeManaged && (
                      <span className='ml-1 text-xs text-blue-600'>(Stripe)</span>
                    )}
                  </div>
                  <div className='grid grid-cols-3 gap-2'>
                    <div>
                      <label className='text-xs font-medium text-gray-600 mb-1 block'>
                        Value ($)
                      </label>
                      <input
                        type='number'
                        className={`w-full px-2 py-1.5 border border-gray-300 rounded text-sm ${isStripeManaged ? 'bg-gray-50 text-gray-500' : ''}`}
                        value={editForm.contract_value_dollars}
                        onChange={e =>
                          setEditForm({ ...editForm, contract_value_dollars: e.target.value })
                        }
                        placeholder='10000'
                        disabled={isStripeManaged}
                      />
                    </div>
                    <div>
                      <label className='text-xs font-medium text-gray-600 mb-1 block'>
                        Schedule
                      </label>
                      <select
                        className={`w-full px-2 py-1.5 border border-gray-300 rounded text-sm ${isStripeManaged ? 'bg-gray-50 text-gray-500' : ''}`}
                        value={editForm.billing_schedule}
                        onChange={e =>
                          setEditForm({ ...editForm, billing_schedule: e.target.value })
                        }
                        disabled={isStripeManaged}
                      >
                        <option value='monthly'>Monthly</option>
                        <option value='quarterly'>Quarterly</option>
                        <option value='annually'>Annually</option>
                      </select>
                    </div>
                    <div>
                      <label className='text-xs font-medium text-gray-600 mb-1 block'>Terms</label>
                      <select
                        className={`w-full px-2 py-1.5 border border-gray-300 rounded text-sm ${isStripeManaged ? 'bg-gray-50 text-gray-500' : ''}`}
                        value={editForm.payment_terms_days}
                        onChange={e =>
                          setEditForm({ ...editForm, payment_terms_days: e.target.value })
                        }
                        disabled={isStripeManaged}
                      >
                        <option value='15'>Net 15</option>
                        <option value='30'>Net 30</option>
                        <option value='60'>Net 60</option>
                      </select>
                    </div>
                  </div>
                </div>
              )
            })()}
          </div>
        </ModalBody>
        <ModalFooter className='px-5 py-3 border-t'>
          <div className='flex items-center justify-between w-full'>
            {editingLicense?.subscription_id && (
              <span className='text-xs text-gray-500'>
                Stripe-managed license. Unlink to enable manual edits.
              </span>
            )}
            <div className='flex gap-2 ml-auto'>
              <Button variant='outlined' onClick={handleCancelEdit} className='h-8 px-4 text-sm'>
                {editingLicense?.subscription_id ? 'Close' : 'Cancel'}
              </Button>
              {!editingLicense?.subscription_id && (
                <Button
                  variant='primary'
                  onClick={handleUpdateLicense}
                  isDisabled={isUpdating}
                  className='h-8 px-4 text-sm'
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
          </div>
        </ModalFooter>
      </Modal>

      <LicenseDetailsModal
        isOpen={selectedLicenseDetails !== null}
        selectedLicenseDetails={selectedLicenseDetails}
        onClose={() => setSelectedLicenseDetails(null)}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={licenseToDelete !== null}
        onClose={() => !isDeleting && setLicenseToDelete(null)}
        hideCloseButton={isDeleting}
        className='w-[450px]'
        overlayClassName='z-[60]'
      >
        <ModalBody>
          <div className='text-center py-4'>
            <div className='text-5xl mb-4'>⚠️</div>
            <h3 className='text-lg font-semibold mb-2'>
              {licenseToDelete?.permanent ? 'Permanently Delete License?' : 'Delete License?'}
            </h3>
            <p className='text-gray-600 mb-4'>
              {licenseToDelete?.permanent ? (
                <>
                  This will <strong className='text-red-600'>permanently delete</strong> the license
                  from the database.
                  <br />
                  <span className='text-red-600 font-semibold'>This action cannot be undone.</span>
                </>
              ) : (
                <>
                  This will <strong>remove the license</strong> from the list.
                  <br />
                  <span className='text-gray-500 text-sm'>
                    Note: Licenses that naturally expire (end date passed) show as
                    &quot;Inactive&quot;.
                  </span>
                </>
              )}
            </p>
            {licenseToDelete && (
              <div className='bg-gray-100 rounded-lg p-3 text-left text-sm mb-4'>
                <div>
                  <span className='text-gray-500'>License:</span>{' '}
                  <strong>{licenseToDelete.license.license_type_label}</strong>
                </div>
                <div>
                  <span className='text-gray-500'>ID:</span>{' '}
                  <span className='font-mono'>{licenseToDelete.license.id}</span>
                </div>
                {licenseToDelete.license.is_billing_period && (
                  <div className='text-purple-600 mt-2'>
                    ⚠️ This is the billing-period license. The next-highest-tier active license (if
                    any) will become the billing period.
                  </div>
                )}
                {licenseToDelete.license.subscription_id && (
                  <div className='text-blue-600 mt-2'>
                    ℹ️ The Stripe subscription will also be canceled.
                  </div>
                )}
              </div>
            )}
            {licenseToDelete?.permanent && (
              <div className='mt-4 text-left'>
                <div className='text-sm text-gray-700 mb-1.5'>
                  Type{' '}
                  <code className='inline-block rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[12px] text-red-700 whitespace-nowrap'>
                    {HARD_DELETE_CONFIRM_PHRASE}
                  </code>{' '}
                  to enable Hard Delete:
                </div>
                <input
                  type='text'
                  value={hardDeleteConfirmText}
                  onChange={e => setHardDeleteConfirmText(e.target.value)}
                  placeholder={HARD_DELETE_CONFIRM_PHRASE}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500'
                  autoComplete='off'
                  spellCheck={false}
                />
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className='flex justify-center gap-3'>
          <Button
            variant='outlined'
            onClick={() => setLicenseToDelete(null)}
            isDisabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant='destructive'
            onClick={handleConfirmDelete}
            isDisabled={
              isDeleting ||
              (licenseToDelete?.permanent === true &&
                hardDeleteConfirmText !== HARD_DELETE_CONFIRM_PHRASE)
            }
            className={licenseToDelete?.permanent ? 'bg-red-700 hover:bg-red-800 text-white' : ''}
          >
            {isDeleting
              ? 'Deleting...'
              : licenseToDelete?.permanent
                ? 'Hard Delete'
                : 'Yes, Delete'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Revoke License Confirmation Modal */}
      <Modal
        isOpen={licenseToRevoke !== null}
        onClose={() => !isRevoking && setLicenseToRevoke(null)}
        hideCloseButton={isRevoking}
        className='w-[450px]'
        overlayClassName='z-[60]'
      >
        <ModalBody>
          <div className='text-center py-4'>
            <div className='text-5xl mb-4'>⏸️</div>
            <h3 className='text-lg font-semibold mb-2'>Revoke License?</h3>
            <p className='text-gray-600 mb-4'>
              This will set the license end date to <strong>now</strong>, making it{' '}
              <strong className='text-orange-600'>inactive</strong> immediately.
              <br />
              <span className='text-gray-500 text-sm'>
                The license will remain visible in the list as &quot;Inactive&quot;.
              </span>
            </p>
            {licenseToRevoke && (
              <div className='bg-gray-100 rounded-lg p-3 text-left text-sm mb-4'>
                <div>
                  <span className='text-gray-500'>License:</span>{' '}
                  <strong>{licenseToRevoke.license_type_label}</strong>
                </div>
                <div>
                  <span className='text-gray-500'>ID:</span>{' '}
                  <span className='font-mono'>{licenseToRevoke.id}</span>
                </div>
                {licenseToRevoke.is_billing_period && (
                  <div className='text-purple-600 mt-2'>
                    ⚠️ This is the billing-period license. The next-highest-tier active license (if
                    any) will become the billing period.
                  </div>
                )}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter className='flex justify-center gap-3'>
          <Button
            variant='outlined'
            onClick={() => setLicenseToRevoke(null)}
            isDisabled={isRevoking}
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handleConfirmRevoke}
            isDisabled={isRevoking}
            className='bg-orange-500 hover:bg-orange-600 text-white'
          >
            {isRevoking ? 'Revoking...' : 'Yes, Revoke'}
          </Button>
        </ModalFooter>
      </Modal>
    </Modal>
  )
}
